import type {
  AppointmentPlanningPreviewAppointment,
  AppointmentPlanningPreviewPayload,
  AppointmentPlanningPreviewRow,
} from "../../../../../../../../api/kehrblatt/planning"
import { formatDateOnly } from "../../../../../_logic/dates"
import { dateOnlyInTimezone } from "../../../../../../../_shared/_logic/timezones"

export type AppointmentPlanningStartBound = {
  startDate: string
  timezone: string
}

type AppointmentStart = Pick<
  AppointmentPlanningPreviewAppointment,
  "start_at" | "flex_type" | "timeframe_start_at"
>

export const PLANNING_START_BOUND_MESSAGE =
  "Termin und Flexzeitraum dürfen nicht vor dem gewählten Startdatum liegen."

export const appointmentMeetsStartBound = (
  appointment: AppointmentStart | null | undefined,
  bound: AppointmentPlanningStartBound,
): boolean => {
  if (!appointment) return true

  const startDate = formatDateOnly(bound.startDate)

  if (!startDate) return false

  const starts = [appointment.start_at]

  if (appointment.flex_type === "SOFT")
    starts.push(appointment.timeframe_start_at ?? appointment.start_at)

  return starts.every((value) => {
    const date = value ? dateOnlyInTimezone(new Date(value), bound.timezone) : null

    return date !== null && date >= startDate
  })
}

export const previewRowMeetsStartBound = (
  row: AppointmentPlanningPreviewRow,
  bound: AppointmentPlanningStartBound,
): boolean =>
  row.effective_planning_strategy === "KEEP_EXISTING" ||
  appointmentMeetsStartBound(row.appointment, bound)

export const invalidatePreviewBeforeStart = (
  rows: AppointmentPlanningPreviewRow[],
  bound: AppointmentPlanningStartBound,
): AppointmentPlanningPreviewRow[] =>
  rows.map((row) =>
    previewRowMeetsStartBound(row, bound)
      ? row
      : {
          ...row,
          status: "unplanned",
          appointment: null,
          warnings: [
            ...row.warnings,
            { code: "before_start_date", severity: "error", message: PLANNING_START_BOUND_MESSAGE },
          ],
        },
  )

export const validatePlanningStartBound = (
  payload: AppointmentPlanningPreviewPayload,
  rows: AppointmentPlanningPreviewRow[],
  bound: AppointmentPlanningStartBound,
): string | null => {
  const startDate = formatDateOnly(bound.startDate)

  if (!startDate) return "Bitte frühesten Planungstag auswählen."

  if (formatDateOnly(payload.start_date) !== startDate)
    return "Bitte das geänderte Startdatum zuerst anwenden."

  const previewById = new Map(rows.map((row) => [row.client_row_id, row]))
  for (const row of payload.rows) {
    const preview = previewById.get(row.client_row_id)

    // Calendar/manual edits switch the strategy to MANUAL; only an unchanged reuse is exempt.
    if (
      row.planning_strategy === "KEEP_EXISTING" &&
      !row.manual_appointment &&
      preview?.effective_planning_strategy === "KEEP_EXISTING"
    )
      continue

    if (
      !appointmentMeetsStartBound(row.manual_appointment, bound) ||
      !appointmentMeetsStartBound(preview?.appointment, bound)
    )
      return PLANNING_START_BOUND_MESSAGE
  }

  return null
}
